/**
 * Graph workflow UI utilities
 * 
 * All graph definitions come from the backend API.
 * This file contains only conversion utilities.
 */

import { getLayoutedElements } from '../utils/layout';

/**
 * Convert backend serialized graph to React Flow format with auto-layout
 * 
 * @param {Object} backendGraph - Serialized graph from backend API
 * @returns {Object} - { nodes, edges, nodeConfigs, stateSchema }
 */
export async function convertBackendGraphToReactFlow(backendGraph) {
  if (!backendGraph || !backendGraph.nodes) {
    return { nodes: [], edges: [], nodeConfigs: {}, stateSchema: null };
  }

  // Convert nodes to React Flow format
  const nodes = backendGraph.nodes.map(nodeDef => {
    const nodeType = nodeDef.type || nodeDef.data?.nodeType || nodeDef.id;

    // START / END terminal markers — rendered as small pills (TerminalNode).
    if (nodeType === 'start' || nodeType === 'end') {
      return {
        id: nodeDef.id,
        type: 'terminal',
        data: {
          terminal: nodeType,
          label: nodeDef.data?.label || (nodeType === 'start' ? 'Start' : 'End'),
        },
        position: nodeDef.position || { x: 0, y: 0 }
      };
    }

    // Check if this is a decision node
    if (nodeType === 'decision') {
      return {
        id: nodeDef.id,
        type: 'decision',
        data: {
          conditional: true
        },
        position: nodeDef.position || { x: 0, y: 0 }
      };
    }
    
    // Regular workflow node - label comes from backend data
    return {
      id: nodeDef.id,
      type: 'workflowNode',
      data: {
        label: nodeDef.data?.label || nodeType,
        nodeType: nodeType,
        status: 'pending',
        configurable: !!backendGraph.nodeConfigs?.[nodeDef.id]?.prompt
      },
      position: nodeDef.position || { x: 0, y: 0 }
    };
  });

  // Convert edges to React Flow format. Drop edges that reference a node not
  // in the node set — serialized graphs include edges to virtual terminals
  // (e.g. `END`/`START`) and conditional routes that may point at ids we don't
  // render as nodes. React Flow errors on edges with a missing source/target,
  // which can blank the whole canvas, so filter them out first.
  const nodeIdSet = new Set(nodes.map((n) => n.id));
  const edges = (backendGraph.edges || [])
    .filter((e) => nodeIdSet.has(e.source) && nodeIdSet.has(e.target))
    .map((edgeDef, index) => {
    const edge = {
      id: edgeDef.id || `e${index + 1}`,
      source: edgeDef.source,
      target: edgeDef.target,
      type: 'smoothstep'
    };
    
    if (edgeDef.label) {
      edge.label = edgeDef.label;
    }
    
    // Preserve source/target handles if they exist in backend data
    if (edgeDef.sourceHandle) {
      edge.sourceHandle = edgeDef.sourceHandle;
    }
    if (edgeDef.targetHandle) {
      edge.targetHandle = edgeDef.targetHandle;
    }
    
    // Handle conditional edges. Calm styling: a thin dashed line in a muted
    // branch color, and NO marching-ants animation — on a long early-exit edge
    // (e.g. `fetch_issues → END` when there are 0 issues) the animation + bright
    // orange read as tangled noise rather than an intentional branch.
    if (edgeDef.data?.conditionalCode) {
      const isValidPath = edgeDef.label?.toLowerCase().includes('valid') &&
                          !edgeDef.label?.toLowerCase().includes('invalid');
      edge.style = {
        stroke: isValidPath ? '#5aa86f' : '#c98a4b',
        strokeWidth: 1.5,
        strokeDasharray: '6 4',
      };
      edge.animated = false;
      edge.data = {
        conditionalCode: edgeDef.data.conditionalCode,
        isConditional: true
      };
    }
    
    return edge;
  });

  // Assign handles for proper connection points
  assignEdgeHandles(nodes, edges);

  // Apply auto-layout if positions are all at origin
  const needsLayout = nodes.every(n => n.position.x === 0 && n.position.y === 0);
  let layoutedNodes = nodes;
  let layoutedEdges = edges;
  
  if (needsLayout) {
    const result = await getLayoutedElements(nodes, edges);
    layoutedNodes = result.nodes;
    layoutedEdges = result.edges;
  }

  return {
    nodes: layoutedNodes,
    edges: layoutedEdges,
    nodeConfigs: backendGraph.nodeConfigs || {},
    stateSchema: backendGraph.stateSchema || null
  };
}

/**
 * Assign source/target handles for proper edge rendering
 * Only assigns handles if they don't already exist (fallback for legacy data)
 */
function assignEdgeHandles(nodes, edges) {
  // Left→right flow: every edge leaves its source's RIGHT side and enters its
  // target's LEFT side. (Decision branches all leave from 'right' and fan out to
  // their differently-placed targets — smoothstep separates them.)
  edges.forEach(edge => {
    if (!edge.sourceHandle) edge.sourceHandle = 'right';
    if (!edge.targetHandle) edge.targetHandle = 'target-left';
  });
}
